<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50 tw-flex items-center justify-center p-4"
      @click.self="close"
    >
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close"/>

      <!-- Modal -->
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] tw-flex flex-col overflow-hidden">

        <!-- Close button -->
        <div class="absolute top-3 right-3 z-10">
          <button
            class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/90 hover:bg-white border-0 cursor-pointer transition-colors text-gray-600 shadow"
            @click="close"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Scrollable content -->
        <div class="overflow-y-auto flex-1">

          <!-- Media -->
          <div class="relative shrink-0" :class="event.image ? 'h-72' : 'h-40'" style="background: var(--primary-community)">
            <img
              v-if="event.image"
              :src="getImageForAnnouncement(event)"
              :alt="event.title"
              class="w-full h-full object-cover"
            />
            <div v-else class="w-full h-full tw-flex items-center justify-center">
              <span class="material-symbols-outlined" style="font-size: 3rem; color: rgba(255,255,255,0.2);">event</span>
            </div>
            <!-- Gradient overlay -->
            <div class="absolute inset-0 pointer-events-none" style="background: linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%)"/>

            <!-- Date badge -->
            <div class="absolute top-3 left-3 bg-white rounded-xl px-3 py-2 tw-flex flex-col items-center shadow-lg" style="min-width: 50px;">
              <span class="bbm-modal-date-day">{{ formatDay(event.start_date) }}</span>
              <span class="bbm-modal-date-month">{{ formatMonth(event.start_date) }}</span>
            </div>

            <!-- Registration chip -->
            <span v-if="event.allow_registration" class="absolute bottom-3 right-3 tw-flex items-center gap-1 bbm-modal-reg-chip">
              <span class="material-symbols-outlined" style="font-size:11px; line-height:1;">how_to_reg</span>
              Registration open
            </span>
          </div>

          <div class="p-6">

            <!-- Organiser -->
            <!--            <p v-if="event.actor_name || event.user" class="bbm-modal-organiser">
                          {{ event.actor_name || event.user?.name }}
                        </p>-->

            <!-- Title -->
            <h2 class="bbm-modal-title">{{ event.title }}</h2>

            <!-- Metas -->
            <div class="bbm-modal-metas">
              <div v-if="event.start_date" class="bbm-modal-meta">
                <span class="material-symbols-outlined bbm-modal-meta-icon">schedule</span>
                <span>
                  {{ formatDateTime(event.start_date) }}
                  <template v-if="event.end_date && event.end_date !== event.start_date">
                    &ndash; {{ formatDateTime(event.end_date) }}
                  </template>
                </span>
              </div>
              <div v-if="location" class="bbm-modal-meta">
                <span class="material-symbols-outlined bbm-modal-meta-icon">location_on</span>
                {{ location }}
              </div>
              <div v-if="event.actor_name || (event.user && event.user?.name)" class="bbm-modal-meta">
                <span class="material-symbols-outlined bbm-modal-meta-icon">person</span>
                {{ event.actor_name || event.user?.name }}
              </div>
            </div>

            <!-- Divider -->
            <div class="bbm-modal-divider"/>

            <!-- Description -->
            <div
              v-if="event.shortDescription || event.body"
              class="bbm-modal-body"
              v-html="event.body || event.shortDescription"
            />

            <!-- Tags -->
            <div v-if="event.tags && event.tags.length" class="tw-flex flex-wrap gap-2 mt-4">
              <span v-for="tag in event.tags" :key="tag.value" class="bbm-modal-tag">{{ tag.label }}</span>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div v-if="event.allow_registration || event.url" class="shrink-0 border-t border-gray-100 px-6 py-4 bg-white tw-flex items-center justify-end gap-3 rounded-b-2xl">
          <a
            v-if="event.url"
            :href="event.url"
            target="_blank"
            rel="noopener noreferrer"
            class="bbm-modal-btn bbm-modal-btn--secondary"
          >
            Event website
          </a>
          <a
            v-if="event.allow_registration && event.registration_url"
            :href="event.registration_url"
            target="_blank"
            rel="noopener noreferrer"
            class="bbm-modal-btn bbm-modal-btn--primary"
          >
            <span class="material-symbols-outlined" style="font-size:15px;">how_to_reg</span>
            Register
          </a>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { getImageForAnnouncement } from '@/util/helpers'

  export default defineComponent({
    name: 'BrusselsEventModal',
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      event: {
        type: Object,
        required: true,
      },
    },
    emits: ['update:modelValue'],
    computed: {
      location(): string {
        const e = this.event as any
        if (e.address_line) return e.address_line
        const addr = e.context?.address
        if (!addr) return ''
        return [addr.city, addr.country].filter(Boolean).join(', ')
      },
    },
    methods: {
      getImageForAnnouncement,
      close() {
        this.$emit('update:modelValue', false)
      },
      formatDay(dateStr: string): string {
        if (!dateStr) return ''
        return new Date(dateStr).toLocaleDateString('en-BE', { day: '2-digit' })
      },
      formatMonth(dateStr: string): string {
        if (!dateStr) return ''
        return new Date(dateStr).toLocaleDateString('en-BE', { month: 'short' }).toUpperCase()
      },
      formatDateTime(dateStr: string): string {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        const date = d.toLocaleDateString('en-BE', { day: 'numeric', month: 'long', year: 'numeric' })
        const time = d.toLocaleTimeString('en-BE', { hour: '2-digit', minute: '2-digit' })
        return `${date} · ${time}`
      },
    },
  })
</script>

<style scoped lang="scss">
  .material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }

  /* Date badge */
  .bbm-modal-date-day {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.35rem;
    font-weight: 800;
    color: var(--primary-community);
    line-height: 1;
  }

  .bbm-modal-date-month {
    font-size: 0.6rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 2px;
  }

  /* Registration chip */
  .bbm-modal-reg-chip {
    background: rgba(22, 101, 52, 0.88);
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 4px 9px;
    border-radius: 9999px;
    backdrop-filter: blur(4px);
  }

  /* Organiser */
  .bbm-modal-organiser {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--primary-community);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 0 0 8px;
  }

  /* Title */
  .bbm-modal-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 16px;
    line-height: 1.3;
  }

  /* Metas */
  .bbm-modal-metas {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .bbm-modal-meta {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.85rem;
    color: #475569;
    line-height: 1.4;
  }

  .bbm-modal-meta-icon {
    font-size: 16px;
    color: #cbd5e1;
    flex-shrink: 0;
    margin-top: 1px;
  }

  /* Divider */
  .bbm-modal-divider {
    height: 1px;
    background: #f1f5f9;
    margin: 20px 0;
  }

  /* Body text */
  .bbm-modal-body {
    font-size: 0.9rem;
    color: #475569;
    line-height: 1.7;

    :deep(p) {
      margin: 0 0 12px;
    }

    :deep(a) {
      color: var(--primary-community);
    }
  }

  /* Tags */
  .bbm-modal-tag {
    font-size: 0.72rem;
    font-weight: 600;
    color: #475569;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: 9999px;
  }

  /* Footer buttons */
  .bbm-modal-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 8px 18px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.85;
    }

    &--primary {
      background: var(--primary-community);
      color: white;
    }

    &--secondary {
      background: #f1f5f9;
      color: #475569;
    }
  }
</style>
